import type { JSX } from 'react';
import { ShieldCheck, Cpu, Gauge, Wrench, MapPin } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { ArticleTeaser } from '@/types';
import { useContent } from '@data';
import { useT } from '@/i18n';
import ArticleTeaserCard from './ArticleTeaserCard';

const ACCENTS = [
  'var(--color-maitrise)',
  'var(--color-piloter)',
  'var(--color-anticiper)',
  'var(--color-ameliorer)',
  'var(--color-ciel)',
  'var(--color-temps)',
  'var(--color-reagir)',
];

const GROUP_ICONS: LucideIcon[] = [ShieldCheck, Cpu, Gauge, Wrench, MapPin];

/** Accueil /articles : tous les articles en cartes numérotées, groupés + menu de thèmes. */
export default function ArticlesIndex(): JSX.Element {
  const { BLOG_GROUPS, ARTICLE_TEASERS } = useContent();
  const t = useT();

  const bySlug = new Map<string, ArticleTeaser>(
    ARTICLE_TEASERS.map((article) => [article.slug, article]),
  );
  const total = BLOG_GROUPS.reduce((sum, group) => sum + group.slugs.length, 0);
  let counter = 0;

  return (
    <main className="ArticleIndex">
      <header className="ArticleIndex__hero">
        <p className="ArticlePage__cat">{t.articles.indexKicker}</p>
        <h1 className="ArticlePage__title">{t.articles.indexTitle}</h1>
        <p className="ArticleWelcome__sub">{t.articles.indexIntro}</p>
        <p className="ArticleIndex__count">{total} {t.articles.indexCount}</p>
      </header>

      <nav className="ArticleIndex__cats" aria-label={t.articles.exploreByTheme}>
        <span className="ArticleIndex__catsLabel">{t.articles.exploreByTheme}</span>
        {BLOG_GROUPS.map((group, i) => {
          const Icon = GROUP_ICONS[i] ?? MapPin;
          return (
            <a key={group.label} className="ArticleIndex__catPill" href={`#cat-${String(i)}`}>
              <Icon size={15} />
              {group.label}
            </a>
          );
        })}
      </nav>

      {BLOG_GROUPS.map((group, i) => {
        const Icon = GROUP_ICONS[i] ?? MapPin;
        return (
          <section className="ArticleIndex__group" id={`cat-${String(i)}`} key={group.label}>
            <h2 className="ArticleIndex__groupTitle">
              <Icon size={20} className="ArticleIndex__groupIcon" />
              {group.label}
            </h2>
            <div className="Articles__grid">
              {group.slugs.map((slug) => {
                const article = bySlug.get(slug);
                if (article === undefined) return null;
                counter += 1;
                return (
                  <ArticleTeaserCard
                    key={slug}
                    article={article}
                    accent={ACCENTS[(counter - 1) % ACCENTS.length]}
                    more={t.sections.articles.readMore}
                    themes={t.sections.articles.chip}
                    num={String(counter).padStart(2, '0')}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </main>
  );
}
